import camelcaseKeys from 'camelcase-keys';
import cache from 'lscache';
import { t } from 'i18next';

import store from '../store/store';
import { cacheErrorLog, createLog } from './logger';
import { buildHeaders, buildRequest, buildUrl, fetchRequest } from '../utils/apis/RestApi';
import { getUserLocale, handle404Errors, handleError } from '../business/helpers';
import { getModelsInformation } from './spid';
import { getProductNatureLabelsByIds } from './dictionaries';
import { minutesToMidnight } from '../utils/utils';
import { POGOS } from '../features/intervention/helpers/pogo';
import { isInsuranceIntervention } from '../business/entityHelper';
import {
  LevelType,
  type ILinkEntityResponse,
  type ILinkProductResponse,
  type Thumbnail,
  type ILinkProcess,
  type Entity,
  type InterventionNature,
  type ILinkSparePartNature,
  type ILinkProduct,
  type ILinkServicesNature,
  type ProductInformation,
  type ILinkNatureCategory,
  type ILinkSparePartsAndServicesNature,
  type IlinkGroupProductNaturesContent,
  type ILinkEntity,
  type CompatibilitySparePartNature,
  type CompatibilityModel,
} from './ilink.type';
import { type ModelInformation } from './spid.type';
import { type ProductNature } from './dictionaries.type';
import { iLinkLifeStages, LifeStage } from '../business/articleHelper';
import { getIsInterventionWarrantyOnly } from '../store/selectors/intervention';
import { isCustomProduct } from '../store/selectors/catalog';
import { isAmiProduct } from '../business/amiProductId';

export const iLinkRanges = [0, 1, 2, 3, 4];

const getCountryId = () => store.getState().userInfo.countryId;
const logger = createLog('api/ilink');

const iLinkStorageKey = 'iLink';

const verifyHasNoResult = (status: number | undefined) => status === 204;

export const NATURE_ID_WITHOUT_SPARE_PART = 9999;

const getNextLevel = (actualLevel: LevelType) => {
  switch (actualLevel) {
    case LevelType.processes:
      return LevelType.categories;
    case LevelType.categories:
      return LevelType.products;
    default:
      return LevelType.product;
  }
};

export const getProductsFromCatalog = (entityId: number): Promise<ILinkEntityResponse[]> => {
  const path = `ilink/v1/categories/${entityId}/products?country_number=${getCountryId()}&include_product_without_spare_parts=false`;
  const url = buildUrl('ICARE_BACK', path);
  const headers = buildHeaders();
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return { product_ids: [] };
      }
      return response.json();
    })
    .then((result: ILinkProductResponse) => result.product_ids.map(id => ({ model_code: id })))
    .catch(error => handle404Errors(error, logger, url, []));
};

const getProcessesOrCategoriesFromCatalog = (processId?: number | null): Promise<ILinkEntityResponse[]> => {
  const countryId = getCountryId();
  const path = processId ? `categories_from_processes/${processId}/${countryId}` : `processes/${countryId}`;
  const url = buildUrl('ICARE_BACK', `ilink/${path}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request).then(response => {
    if (verifyHasNoResult(response.status)) {
      return [];
    }
    return response.json();
  });
};

const furtherRanking = 999;

const orderEntitiesByRanking = (ilinkEntities: ILinkEntityResponse[]) => {
  const entities: ILinkEntity[] = ilinkEntities.map(entity => {
    const id = entity.id || entity.model_code;

    return {
      id,
      label: entity.label || entity.id?.toString() || '',
      imageUrl: entity.image_url,
      ranking: entity.ranking,
      isCustom: isAmiProduct(id),
    };
  });

  return entities
    .reduce<(typeof entities)[]>(
      (orderedEntities, entity) => {
        const getPosition = () => {
          if (!entity.ranking) return 1;
          return entity.ranking === furtherRanking ? 2 : 0;
        };

        orderedEntities[getPosition()].push(entity);
        return orderedEntities;
      },
      [[], [], []]
    )
    .flatMap((entity, index) =>
      index === 0 ? entity.toSorted((a, b) => (a.ranking ?? 0) - (b.ranking ?? 0)) : entity.toSorted((a, b) => a.label.localeCompare(b.label))
    );
};

const getILinkCatalog = (actualLevel: LevelType, entityId?: number | null): Promise<Thumbnail> => {
  const entityKey = entityId ? `-${entityId}` : '';
  const key = `${iLinkStorageKey}-${actualLevel}${entityKey}`;
  const storedCatalog = cache.get(key);

  if (storedCatalog) {
    return Promise.resolve(JSON.parse(storedCatalog));
  }

  const promise = actualLevel === LevelType.products && entityId ? getProductsFromCatalog(entityId) : getProcessesOrCategoriesFromCatalog(entityId);

  return promise
    .then((ilinkEntities: ILinkEntityResponse[]) => {
      const entities =
        actualLevel === LevelType.products
          ? ilinkEntities.map(ilinkEntity => ({
              id: ilinkEntity.model_code,
              label: ilinkEntity.label,
              isCustom: isAmiProduct(ilinkEntity.model_code),
            }))
          : orderEntitiesByRanking(ilinkEntities);
      return {
        nextLevel: getNextLevel(actualLevel),
        entities,
      };
    })
    .then(thumbnail => {
      if (thumbnail.nextLevel === LevelType.product) {
        const entitiesIndex = thumbnail.entities.reduce((acc: Record<string, boolean>, curr) => {
          acc[curr.id.toString()] = true;
          return acc;
        }, {});

        return getModelsInformation(thumbnail.entities.map(entity => entity.id)).then(entities => ({
          ...thumbnail,
          entities: [...entities]
            .sort((a, b) => (b.saleYear ?? 0) - (a.saleYear ?? 0))
            .reduce((acc: ILinkEntity[], entity: ModelInformation) => {
              if (entitiesIndex[entity.modelCode]) {
                acc.push({
                  id: entity.modelCode,
                  label: entity.label,
                  imageUrl: entity.imageUrl,
                  isCustom: isAmiProduct(entity.modelCode),
                });
              }
              return acc;
            }, []),
        }));
      }

      return thumbnail;
    })
    .then(thumbnail => {
      try {
        cache.set(key, JSON.stringify(thumbnail), minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return thumbnail;
    })
    .catch(() => handleError('ILink Request - GET catalog entities'));
};

export const getILinkProcesses = (): Promise<ILinkProcess[]> =>
  getILinkCatalog(LevelType.processes).then(processes => {
    const formattedProcesses: ILinkProcess[] = processes.entities.map(process => ({
      id: process.id,
      name: process.label || process.id?.toString(),
    }));
    return formattedProcesses;
  });

export const chunkArray = <T>(array: T[], chunkSize: number): Array<T[]> => {
  const results = [];
  const arrayToChunk = [...array];
  while (arrayToChunk.length) {
    results.push(arrayToChunk.splice(0, chunkSize));
  }
  return results;
};

export const articleBasedOnType = (): { gridValueId: string; free: boolean } => {
  const state = store.getState();
  const isWarrantyOnly = getIsInterventionWarrantyOnly(state);
  const type = state.intervention.interventionType;

  if (isWarrantyOnly) {
    return {
      gridValueId: POGOS.WARRANTY.masterdataId,
      free: true,
    };
  }

  if (isInsuranceIntervention(type)) {
    return {
      gridValueId: POGOS.OGEA.masterdataId,
      free: true,
    };
  }

  return {
    gridValueId: POGOS.PAYING.masterdataId,
    free: false,
  };
};

const buildEntity = (model: ModelInformation): Entity => ({
  modelCode: model.modelCode,
  name: model.label,
  imageUrl: model.imageUrl,
  quantity: 0,
  articles: [],
});

export const getSparePartsByIds = (sparePartsIds: number[], related?: boolean): Promise<Entity[]> =>
  getModelsInformation(sparePartsIds).then(entities => entities.map(entity => ({ ...buildEntity(entity), related })));

const sortNatures = (entities: InterventionNature[]) => [...entities].sort((a, b) => a.natureTitle.localeCompare(b.natureTitle));

const sortAndCacheNatureEntities = (entities: InterventionNature[], key: string) => {
  const sortedNatures = sortNatures(entities);
  try {
    cache.set(key, sortedNatures, minutesToMidnight());
  } catch (error) {
    cacheErrorLog(error);
  }
  return sortedNatures;
};

export const getHasSpareParts = (productId?: string, categoryId?: number): Promise<boolean> => {
  let key = '';
  let url = '';
  const pathParams: string = `/spare_parts?include_hypothesis=true&country_number=${getCountryId()}&life_stage=${iLinkLifeStages}`;
  const headers = buildHeaders(true);
  headers.set('accept-language', '');

  if (productId) {
    key = `${iLinkStorageKey}-product-${productId}-has-spare-parts`;
    url = buildUrl('ICARE_BACK', `ilink/v2/products/${productId}${pathParams}`);
  } else {
    if (categoryId == null) {
      return Promise.resolve(false);
    }
    key = `${iLinkStorageKey}-category-${categoryId}-has-spare-parts`;
    url = buildUrl('ICARE_BACK', `ilink/v2/categories/${categoryId}${pathParams}`);
  }

  const storedHasSpareParts = cache.get(key);
  if (storedHasSpareParts) {
    return Promise.resolve(storedHasSpareParts);
  }

  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return [];
      }
      return response.json();
    })
    .then((iLinkNatures: ILinkSparePartNature[]) => {
      const result = !!iLinkNatures.length;
      try {
        cache.set(key, result, minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return result;
    })
    .catch(() => handleError('ILink Request - GET has spare parts'));
};

export const getSparePartsByServiceId = (serviceId: number): Promise<{ spareParts: Entity[]; natureId: number }> => {
  const url = buildUrl('ICARE_BACK', `ilink/v1/services/${serviceId}/spare_parts?country_number=${getCountryId()}&life_stage=${iLinkLifeStages}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return null;
      }
      return response.json();
    })
    .then((sparePartsNatures: ILinkSparePartNature[] | null) => {
      if (sparePartsNatures === null) {
        return { spareParts: [], natureId: NATURE_ID_WITHOUT_SPARE_PART };
      }

      const sparePartIds = sparePartsNatures.flatMap(sparePartNature => sparePartNature.spare_part_models);
      return getSparePartsByIds(sparePartIds).then(spareParts => ({
        spareParts,
        natureId: sparePartsNatures[0].spare_part_nature_id,
      }));
    })
    .catch(() => handleError('ILink Request - GET spare parts by service id'));
};

export const getSparePartsByCategoryAndNature = (categoryId: number, natureId: number, isProductCustom = false): Promise<Entity[]> => {
  const key = `${iLinkStorageKey}-spareParts-from-category-${categoryId}-and-nature-${natureId}${isProductCustom && '-customProduct'}`;
  const storedSparePartModels = cache.get(key);
  if (storedSparePartModels) {
    return Promise.resolve(storedSparePartModels);
  }
  const locale = getUserLocale();
  const headers = buildHeaders(true);
  const url = buildUrl('ICARE_BACK', `ilink/v1/spareparts_by_category_and_nature/${locale}/${getCountryId()}/${categoryId}/${natureId}`);
  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => response.json())
    .then((spareParts: Entity[]) => {
      let result = spareParts;
      if (isProductCustom) {
        const partsToDisplay = [...spareParts].slice(0, 4).map(sparePart => ({ ...sparePart, related: false }));
        result = [...partsToDisplay, ...spareParts.slice(4)];
      }
      try {
        cache.set(key, result, minutesToMidnight());
      } catch (error) {
        cacheErrorLog(error);
      }
      return result;
    });
};

const buildNatureSparePart = (natureSparePart: Entity, compatibilityModelSpareParts: CompatibilityModel[]): Entity => {
  const compatibilitySparePart = compatibilityModelSpareParts.find(comp => comp.spare_part_id === natureSparePart.modelCode);

  return {
    ...natureSparePart,
    compatibility: compatibilitySparePart?.compatibility,
    hypothesis: compatibilitySparePart?.hypothesis,
  };
};

const buildCompatibilityInterventionNature = (
  iLinkNature: CompatibilitySparePartNature,
  natures: ProductNature[],
  spareParts: Entity[]
): InterventionNature => {
  const compatibilityModelSpareParts = iLinkNature.spare_part_models;
  const natureSpareParts = spareParts.filter(sparePart => compatibilityModelSpareParts.some(model => model.spare_part_id === sparePart.modelCode));
  const natureToGet = natures.find(nature => iLinkNature.spare_part_nature_id === nature.id);

  return {
    natureId: iLinkNature.spare_part_nature_id,
    natureTitle: natureToGet?.value ?? iLinkNature.spare_part_nature_id.toString(),
    spareParts: natureSpareParts.map(natureSparePart => buildNatureSparePart(natureSparePart, compatibilityModelSpareParts)),
    services: [],
  };
};

export const getCompatibilitySpareParts = (productId: number): Promise<InterventionNature[]> => {
  const key = `${iLinkStorageKey}-v2-spare-parts-${productId}`;
  const headers = buildHeaders();
  headers.set('accept-language', '');
  const url = buildUrl(
    'ICARE_BACK',
    `ilink/v2/products/${productId}/spare_parts?include_hypothesis=true&country_number=${getCountryId()}&life_stage=${iLinkLifeStages}`
  );
  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return [];
      }
      return response.json();
    })
    .then((compatibilitySparePartNature: CompatibilitySparePartNature[]) => {
      const natureIds = compatibilitySparePartNature.map(nature => nature.spare_part_nature_id);
      const compatibilitySpareParts = compatibilitySparePartNature.flatMap(nature => nature.spare_part_models);

      return Promise.all([
        getProductNatureLabelsByIds(natureIds),
        getSparePartsByIds(compatibilitySpareParts.map(sparePart => sparePart.spare_part_id)),
      ]).then(([natures, spareParts]) =>
        compatibilitySparePartNature.map(iLinkNature => buildCompatibilityInterventionNature(iLinkNature, natures, spareParts))
      );
    })
    .then(natureEntities => sortAndCacheNatureEntities(natureEntities, key))
    .catch(() => handleError('ILink Request - GET compatible spare parts'));
};

export const getSpareParts = async (
  product: ILinkProduct,
  categoryId?: number | null,
  isStoreCategoryDifferent?: boolean,
  hasSparePartsCapability?: boolean
): Promise<InterventionNature[]> => {
  let key = `${iLinkStorageKey}-category-spare-parts-${categoryId}`;
  let path = `categories/${categoryId}`;
  if (!isCustomProduct(product) && !isStoreCategoryDifferent && hasSparePartsCapability) {
    key = `${iLinkStorageKey}-spare-parts-${product.id}`;
    path = `products/${product.id}`;
  }

  if (path.includes('undefined')) {
    const state = store.getState();
    void logger.log(new Error('Category is not defined, cannot get spare parts'), {
      productId: product.id,
      custom: product.isCustom,
      docNumber: state.intervention?.footer?.docNumber,
    });

    return [];
  }

  const storedSpareParts = cache.get(key);
  if (storedSpareParts) {
    return storedSpareParts;
  }

  const headers = buildHeaders(true);
  headers.set('accept-language', '');
  const url = buildUrl(
    'ICARE_BACK',
    `ilink/v1/${path}/spare_parts?include_hypothesis=true&country_number=${getCountryId()}&life_stage=${iLinkLifeStages}`
  );

  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return [];
      }
      return response.json();
    })
    .then((iLinkSparePartsNatures: ILinkSparePartNature[]) => {
      const natureIds = iLinkSparePartsNatures.map(nature => nature.spare_part_nature_id);
      const sparePartIds = iLinkSparePartsNatures.flatMap(nature => nature.spare_part_models);

      return Promise.all([getProductNatureLabelsByIds(natureIds), getSparePartsByIds(sparePartIds)]).then<InterventionNature[]>(
        ([natures, spareParts]) =>
          iLinkSparePartsNatures.map(iLinkNature => {
            const natureSpareParts = spareParts.filter(sparePart => iLinkNature.spare_part_models.includes(sparePart.modelCode));
            const natureToGet = natures.find(nature => iLinkNature.spare_part_nature_id === nature.id);
            return {
              natureId: iLinkNature.spare_part_nature_id,
              natureTitle: natureToGet?.value ?? iLinkNature.spare_part_nature_id.toString(),
              services: [],
              spareParts: natureSpareParts.map(sparePart => ({
                ...sparePart,
              })),
            };
          })
      );
    })
    .then(natureEntities => sortAndCacheNatureEntities(natureEntities, key))
    .catch(() => handleError('ILink Request - GET spare parts'));
};

export const getNaturesBySparePartAndServiceIds = (sparePartIds: number[], serviceIds: number[]): Promise<ILinkSparePartsAndServicesNature> => {
  const basePath = new URLSearchParams();
  serviceIds.length && basePath.set('services', serviceIds.toString());
  sparePartIds.length && basePath.set('spare_parts', sparePartIds.toString());
  const url = buildUrl('ICARE_BACK', `ilink/natures_by_spareparts_and_services?${basePath}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return { spareParts: [], services: [] };
      }
      return response.json();
    })
    .catch(() => handleError('ILink Request - GET nature ID by service Ids and spare parts Ids'));
};

export const getServices = (product: ILinkProduct, categoryId?: number | null, isStoreCategoryDifferent?: boolean): Promise<InterventionNature[]> => {
  let key = `${iLinkStorageKey}-category-services-${categoryId}`;
  let path = `categories/${categoryId}`;

  if (!isCustomProduct(product) && !isStoreCategoryDifferent) {
    key = `${iLinkStorageKey}-services-${product.id}`;
    path = `products/${product.id}`;
  }
  if (path.includes('undefined')) {
    const state = store.getState();
    void logger.log(new Error('Category is not defined, cannot get services'), {
      productId: product?.id,
      custom: product?.isCustom,
      docNumber: state.intervention?.footer?.docNumber,
    });

    return Promise.resolve([]);
  }

  const storedServices = cache.get(key);
  if (storedServices) {
    return Promise.resolve(storedServices);
  }

  const builtUrl = `ilink/v1/${path}/services?country_number=${getCountryId()}&range=${iLinkRanges}&life_stage=${LifeStage.active}`;
  const url = buildUrl('ICARE_BACK', builtUrl);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  const withoutNatureLabel = t('dashboard.results.nature.empty').toUpperCase();
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return [];
      }
      return response.json();
    })
    .then((iLinkNatures: ILinkServicesNature[]) => {
      const serviceIdsPromise = getModelsInformation([...new Set(iLinkNatures.flatMap(nature => nature.service_ids))]).then(entities =>
        entities.map(buildEntity)
      );
      const natureIds = [
        ...new Set(
          iLinkNatures.filter(nature => nature.spare_part_nature_id !== NATURE_ID_WITHOUT_SPARE_PART).map(nature => nature.spare_part_nature_id)
        ),
      ];
      const naturesPromise = getProductNatureLabelsByIds(natureIds);
      return Promise.all([serviceIdsPromise, naturesPromise]).then<InterventionNature[]>(([services, natures]) =>
        iLinkNatures.map(nature => ({
          natureId: nature.spare_part_nature_id,
          natureTitle:
            nature.spare_part_nature_id === NATURE_ID_WITHOUT_SPARE_PART
              ? withoutNatureLabel
              : natures.find(n => n.id === nature.spare_part_nature_id)?.value || nature.spare_part_nature_id.toString(),
          services: services.filter(service => nature.service_ids.includes(service.modelCode)).sort((a, b) => a.name.localeCompare(b.name)),
          spareParts: [],
        }))
      );
    })
    .then(servicesNatures => sortAndCacheNatureEntities(servicesNatures, key))
    .catch(() => handleError('ILink Request - GET services'));
};

export const getProcessByService = (serviceId: number): Promise<ILinkProcess> => {
  const url = buildUrl('ICARE_BACK', `ilink/v1/services/${serviceId}/categories?country_number=${getCountryId()}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => response.json())
    .then(iLinkProcess => {
      const process: ILinkProcess = {
        name: iLinkProcess[0].process_label,
        id: iLinkProcess[0].process_id,
      };

      return process;
    })
    .catch(() => handleError('ILink Request - GET process by service'));
};

export const getProductInformation = (productModelCode: number): Promise<ProductInformation | null> => {
  const key = `${iLinkStorageKey}-product-information-${productModelCode}`;
  const storedProductInformation = cache.get(key);
  if (storedProductInformation) {
    return Promise.resolve(JSON.parse(storedProductInformation));
  }

  const url = buildUrl('ICARE_BACK', `ilink/v1/products/${productModelCode}/categories?country_number=${getCountryId()}`);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return null;
      }
      return response.json();
    })
    .then(processes => {
      const result: ProductInformation | null =
        processes?.length > 0
          ? {
              process: {
                id: processes[0].process_id,
                name: processes[0].process_label,
              },
              category: {
                id: processes[0].categories[0].id,
                name: processes[0].categories[0].label,
              },
            }
          : null;
      if (result) {
        try {
          cache.set(key, JSON.stringify(result), minutesToMidnight());
        } catch (error) {
          cacheErrorLog(error);
        }
      }
      return result;
    })
    .catch(error => {
      if (error?.response?.status === 404) {
        return null;
      }
      return handleError('ILink Request - GET product info');
    });
};

export const getILinkNatureCategories = (processId?: number | null): Promise<ILinkNatureCategory[]> => {
  const key = `${iLinkStorageKey}-nature-categories-${processId ? processId.toString() : ''}`;
  const storedProductInformation = cache.get(key);

  if (storedProductInformation) {
    return Promise.resolve(JSON.parse(storedProductInformation));
  }

  let path = 'ilink/v1/group_product_natures';
  if (processId) {
    path = `${path}?specific_to_processes=${processId}`;
  }
  const url = buildUrl('ICARE_BACK', path);
  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (verifyHasNoResult(response.status)) {
        return { content: [] };
      }
      return response.json();
    })
    .then(({ content }) => camelcaseKeys(content, { deep: true }))
    .then((iLinkNatureCategories: IlinkGroupProductNaturesContent[]) =>
      iLinkNatureCategories.map((iLinkNatureCategory: any) => ({
        id: iLinkNatureCategory.id,
        label: iLinkNatureCategory.label.toUpperCase(),
        imageUrl: iLinkNatureCategory.imageUrl,
        natureIds: iLinkNatureCategory.groupProductNatureAssociations.map((nature: any) => nature.productNatureId),
      }))
    )
    .then(result => {
      if (result.length > 0) {
        try {
          cache.set(key, JSON.stringify(result), minutesToMidnight());
        } catch (error) {
          cacheErrorLog(error);
        }
      }
      return result;
    })
    .catch(() => handleError('ILink Request - GET spare parts categories'));
};
